import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, Users, Video, MapPin } from "lucide-react";
import { api } from "@/lib/api/client";
import { PageHeader } from "@/components/ui/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Pagination, DEFAULT_PAGE_SIZE } from "@/components/ui/Pagination";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";

type MeetingKind = "personal" | "org" | "rooms";

const TITLES: Record<MeetingKind, { title: string; breadcrumb: string; subtitle: string }> = {
  personal: {
    title: "Lịch họp cá nhân",
    breadcrumb: "Quản lý lịch họp › Cá nhân",
    subtitle: "Các cuộc họp bạn tham gia",
  },
  org: {
    title: "Lịch họp đơn vị",
    breadcrumb: "Quản lý lịch họp › Đơn vị",
    subtitle: "Tất cả cuộc họp trong hệ thống",
  },
  rooms: {
    title: "Quản lý phòng họp",
    breadcrumb: "Quản lý lịch họp › Phòng họp",
    subtitle: "Danh sách phòng họp vật lý + trực tuyến",
  },
};

async function fetchMeetings(kind: MeetingKind, page: number, pageSize: number) {
  const url =
    kind === "personal"
      ? "/meeting/personal"
      : kind === "rooms"
        ? "/meeting/rooms"
        : "/meeting/list";
  const { data } = await api.get(url, { params: { page, pageSize } });
  return data.data ?? data;
}

export function MeetingListPage({ kind }: { kind: MeetingKind }) {
  const nav = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const { data, isLoading } = useQuery({
    queryKey: ["meeting", kind, page],
    queryFn: () => fetchMeetings(kind, page, pageSize),
  });

  if (kind === "rooms") {
    const rooms: {
      MEETING_RESOURCE_ID: number;
      CODE: string | null;
      NAME: string | null;
      SCALE: number | null;
      LOCATION: string | null;
      HAS_VIDEO_CONFERENCE: number | null;
    }[] = Array.isArray(data) ? data : [];
    return (
      <div>
        <PageHeader
          title={TITLES.rooms.title}
          subtitle={`${rooms.length} phòng`}
          breadcrumb={TITLES.rooms.breadcrumb}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rooms.map((r) => (
            <Card key={r.MEETING_RESOURCE_ID}>
              <CardHeader>
                <CardTitle>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary-600" />
                    {r.NAME ?? r.CODE ?? `Phòng #${r.MEETING_RESOURCE_ID}`}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardBody className="space-y-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Mã:</span>{" "}
                  <span className="font-mono text-xs">{r.CODE ?? "—"}</span>
                </div>
                {r.LOCATION && (
                  <div>
                    <span className="text-muted-foreground">Vị trí:</span> {r.LOCATION}
                  </div>
                )}
                <div className="flex items-center gap-3 text-xs pt-2 border-t border-border">
                  {r.SCALE && (
                    <span className="inline-flex items-center gap-1">
                      <Users className="h-3 w-3" /> {r.SCALE} người
                    </span>
                  )}
                  {r.HAS_VIDEO_CONFERENCE === 1 && (
                    <span className="inline-flex items-center gap-1 text-primary-600">
                      <Video className="h-3 w-3" /> Video
                    </span>
                  )}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const items = data?.items ?? [];
  const columns: Column<{
    ID: number;
    TITLE: string | null;
    START_TIME: string | null;
    END_TIME: string | null;
    CONTACT: string | null;
    SHOW_PRESIDENT: string | null;
    STATE: number | null;
    CREATOR_NAME: string | null;
  }>[] = [
    {
      key: "stt",
      header: "#",
      width: "48px",
      align: "center",
      render: (_r, i) => (
        <span className="text-muted-foreground tabular-nums">{(page - 1) * 25 + i + 1}</span>
      ),
    },
    {
      key: "title",
      header: "Tiêu đề",
      render: (r) => <span className="font-medium truncate">{r.TITLE ?? "—"}</span>,
    },
    {
      key: "pres",
      header: "Chủ trì",
      width: "200px",
      render: (r) => <span className="text-sm truncate">{r.SHOW_PRESIDENT ?? "—"}</span>,
    },
    {
      key: "start",
      header: "Bắt đầu",
      width: "160px",
      render: (r) => <span className="text-sm tabular-nums">{r.START_TIME ?? "—"}</span>,
    },
    {
      key: "end",
      header: "Kết thúc",
      width: "160px",
      render: (r) => <span className="text-sm tabular-nums">{r.END_TIME ?? "—"}</span>,
    },
    {
      key: "state",
      header: "TT",
      width: "90px",
      render: (r) => (
        <StatusBadge variant={r.STATE === 1 ? "primary" : r.STATE === 3 ? "success" : "neutral"}>
          {r.STATE === 1 ? "Đã lên" : r.STATE === 3 ? "Đã họp" : String(r.STATE ?? "—")}
        </StatusBadge>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={TITLES[kind].title}
        subtitle={`${(data?.total ?? 0).toLocaleString("vi-VN")} cuộc họp`}
        breadcrumb={TITLES[kind].breadcrumb}
        actions={
          // Nút này TRƯỚC ĐÂY không có onClick nên bấm vào không xảy ra gì —
          // hứa một chức năng chưa tồn tại. API POST /api/meeting hiện vẫn là
          // bản rỗng trả 404, nên vô hiệu hoá kèm giải thích thay vì để người
          // dùng bấm rồi tưởng hệ treo.
          kind === "personal" ? (
            <Button
              variant="default"
              size="md"
              disabled
              title="Chức năng tạo cuộc họp chưa mở trong bản này"
            >
              <CalendarDays className="h-4 w-4 mr-1.5" /> Tạo cuộc họp
            </Button>
          ) : undefined
        }
      />
      <DataTable
        columns={columns}
        rows={items}
        keyBy={(r) => r.ID}
        onRowClick={(r) => nav(`/meetings/${r.ID}`)}
        isLoading={isLoading}
        dense
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />
    </div>
  );
}
